import { isAppModule } from '@utils/constants';
import { isPercentileMetric } from '@utils/percentile';
import { metricsStore } from '@globalStore/metricsStore';
import { generateFlowMetrics, generateFrustrationSignalMetrics } from './useSupportedMetrics';

const filterOutPercentileMetrics = (metrics: Record<string, any>, limitation: any) => {
  const filterMetrics = {} as Record<string, any>;
  // Currently, only Service Performance Preset support percentile metrics
  if (limitation?.metrics?.config?.showPercentile) {
    return metrics;
  }
  for (const key in metrics) {
    if (!isPercentileMetric(metrics[key])) {
      filterMetrics[key] = metrics[key];
    }
  }
  return filterMetrics;
};

const filterMetricsCategory = (metrics: Record<string, any> = {}, excludeCategories: string[]) => {
  if (!excludeCategories?.length) {
    return metrics;
  }

  const filterMetrics = {} as Record<string, any>;
  for (const [key, metric] of Object.entries(metrics)) {
    if (!excludeCategories.includes(metric.category)) {
      filterMetrics[key] = metric;
    }
  }
  return filterMetrics;
};

// Utility function to apply limitation filters to flat metrics
function applyLimitationFiltersToMetrics(
  metrics: Record<string, any>,
  excludeCategories: string[] = [],
  excludeKeys: string[] = [],
  excludeTypes: string[] = [],
  limitation: any = null,
): Record<string, any> {
  const filterMetrics = {} as Record<string, any>;

  // Combine parameter exclusions with limitation exclusions
  const allExcludeCategories = [
    ...excludeCategories,
    ...(limitation?.metrics?.excludeCategories || []),
  ];
  const allExcludeKeys = [...excludeKeys, ...(limitation?.metrics?.excludeKeys || [])];
  const allExcludeTypes = [...excludeTypes, ...(limitation?.metrics?.excludeTypes || [])];

  for (const key in metrics) {
    const { type: metricType, category: metricCategory } = metrics[key] || {};
    if (
      !allExcludeKeys.includes(key) &&
      !allExcludeTypes.includes(metricType) &&
      !allExcludeCategories.includes(metricCategory)
    ) {
      filterMetrics[key] = metrics[key];
    }
  }

  return filterMetrics;
}

export const useSupportedSecondaryMetrics = ({
  metrics = {},
  limitation = {},
  flowId = null,
  globalExcludeCategories,
}: {
  metrics: Record<string, any>;
  limitation: any;
  flowId: any;
  globalExcludeCategories: any[];
}) => {
  if (isAppModule) {
    metrics = filterOutPercentileMetrics(metrics, limitation);

    if (!limitation) {
      const filterMetrics = filterMetricsCategory(metrics, globalExcludeCategories);
      return filterMetrics;
    }

    if (limitation?.context?.type === 'flow' && limitation?.context?.id) {
      const currentFlowMeta = metricsStore.getState()?.flowConfigMeta?.[limitation?.context?.id];
      return generateFlowMetrics(metrics, currentFlowMeta);
    }

    if (limitation?.context?.type === 'frustration-signal' && limitation?.context?.id) {
      const currentFrustrationSignalMeta =
        metricsStore.getState()?.frustrationSignalConfigMeta?.[limitation?.context?.id];
      const frustrationSignalMetrics = generateFrustrationSignalMetrics(
        metrics,
        currentFrustrationSignalMeta,
      );

      // Apply the updated frustration signal metrics back to the metrics object
      const updatedMetrics = {
        ...metrics,
        ...frustrationSignalMetrics,
      };

      // Apply limitation filtering using the utility function
      return applyLimitationFiltersToMetrics(updatedMetrics, [], [], [], limitation);
    }

    // Apply limitation filtering using the utility function
    const filteredMetrics = applyLimitationFiltersToMetrics(metrics, [], [], [], limitation);

    // Special logic for flow metrics - add them even if they were filtered out
    if (flowId) {
      for (const key in metrics) {
        if (metrics[key]?.flowID === flowId && !filteredMetrics[key]) {
          filteredMetrics[key] = metrics[key];
        }
      }
    }

    return filteredMetrics;
  }

  return metrics;
};

export default useSupportedSecondaryMetrics;
